<template>
  <v-dialog
    v-model="show"
    max-width="800px"
  >
    <v-form ref="form" v-model="valid" lazy-validation :disabled="loading" @submit="saveOption" onsubmit="return false;">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ isNew ? $t('editor.option.create') :  $t('editor.option.update') }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="show = false"
          >
          <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col
                :cols="`{{ showLogicalLanguage ? 12 : 6 }}`"
              >
                <v-text-field
                  :label="$t('editor.option.name')"
                  required
                  v-model="optionForm.name"
                  outlined
                  clearable
                  counter="100"
                  maxLength="100"
                  :rules="nameRule"
                ></v-text-field>
              </v-col>
              <v-col
                cols="6"
                v-if="showLogicalLanguage"
              >
                <v-text-field
                  :label="$t('editor.option.prolog')"
                  v-model="optionForm.prolog"
                  outlined
                  clearable
                  counter="200"
                  maxLength="200"
                  :rules="prologRule"
                  @click:clear="suggestDefaultProlog = true"
                >
                </v-text-field>
              </v-col>
              <v-col
                cols="12"
                sm="6"
                v-if="isNew"
              >
                <v-switch
                  :label="$t('editor.option.createNegation')"
                  v-model="createNegation"
                />
              </v-col>
            </v-row>
            <v-row v-if="!isNew">
              <v-col cols="12">
                <div>{{ $t('editor.createdBy') }} <strong>{{ optionForm.createdBy }}</strong>, {{ $t('editor.the') }} <strong>{{ new Date(optionForm.createdDate).toLocaleString() }}</strong></div>
                <div>{{ $t('editor.lastModifiedBy') }}  <strong>{{ optionForm.lastModifiedBy }}</strong>, {{ $t('editor.the') }} <strong>{{ new Date(optionForm.lastModifiedDate).toLocaleString() }}</strong></div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn 
            v-if="!isNew"
            class="raison-btn"
            @click="deleteOption"
            :disabled="loading"
            alt="delete"
          >
            <v-icon>
              mdi-trash-can-outline
            </v-icon>
            {{ $t('editor.option.delete') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            class="raison-btn"
            type="submit"
            :disabled="loading || !valid"
          >
            <v-icon>
              mdi-content-save-outline
            </v-icon>
            {{ $t('editor.option.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import optionService from '@/services/options.js'
import translationsService from '@/services/translations.js'
import naturalOrLogicalLanguageMixin from '@/mixins/natural-or-logical-language-mixin.js';

export default ({
  name: 'OptionForm',

  components: {
  },

  mixins: [ naturalOrLogicalLanguageMixin ],

  data () {
    return {
      loading: false,
      optionForm: {},
      createNegation: false,
      valid: false,
      name: this.option.name,
      nameRule: [
        v => !!v || this.$t('global.required'),
        v => /^.{1,100}$/.test(v) || this.$t('editor.option.invalid'),
      ],
      prolog: this.option.prolog,
      prologRule: [
        v => /^.{1,200}$/.test(v) || this.$t('editor.option.invalid'),
        v => v === '' || !/:-/.test(v) || this.$t('editor.element.ruleNotAllowed'),
      ],
      suggestDefaultProlog: true,
    }
  },

  watch: {
    option: function(option) {
      this.optionForm = {
        ...this.optionForm,
        ...option
      }
      this.suggestDefaultProlog = !this.optionForm.id
    },
    "optionForm.name": function(value) {
      if(this.shouldSuggestDefaultPrologTranslation) {
        this.optionForm.prolog = translationsService.getDefaultTranslation(value)
      }
    }
  },

  props: {
    open: {
      type: Boolean,
      required: true,
    },
    option: {
      type: Object,
      required: false,
      default: () => { return {name:'', prolog:'' } }
    }
  },
  
  computed: {
    isNew() {
      return !this.optionForm.id
    },
    shouldSuggestDefaultPrologTranslation() {
      return this.isNew || this.suggestDefaultProlog
    },
    show: {
      get () {
          return this.open;
      },
      set (value) {
        if(!value) {
          this.onCancel()
        }
        this.$emit('update:open', value);
      }
    }
  },

  methods: {
    saveOption(event) {
      event.preventDefault()
      if(!this.$refs.form.validate()) {
        return
      }
      if(this.isNew) {
        this.createOption()
      }
      else {
        this.updateOption()
      }
    },
    createOption() {
      const projectId = this.$route.params.id
      const newOption = { ... this.optionForm }
      this.loading = true
      optionService.createOption(projectId, newOption, this.createNegation)
        .then(response => {
          this.$emit("optionsCreated", response.data)
          this.show = false
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
        .finally(() => {
            this.loading = false
        })
    },
    updateOption() {
      const projectId = this.$route.params.id
      const newOption = { ... this.optionForm }
      this.loading = true
      optionService.updateOption(projectId, newOption)
        .then(response => {
          this.$emit("optionsUpdated", [response.data])
          this.show = false
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
        .finally(() => {
            this.loading = false
        })
    },
    deleteOption() {
      this.loading = true
      const projectId = this.$route.params.id
      const optionToDelete = this.option
      optionService.deleteOption(projectId, optionToDelete.id)
        .then(() => {
          this.$emit("optionsDeleted", [optionToDelete])
          this.show = false
        })
        .catch(err => {
          this.$emit('raison-event', { type: 'error', messages: [err] })
        })
        .finally(() => {
          this.loading = false
        })
    },
    onCancel() {
      this.optionForm = {...this.option}
      this.suggestDefaultProlog = false
      this.createNegation = false
      this.$refs.form.resetValidation()
    }
  },

});
</script>
<style scoped>
</style>